/* eslint-disable */
// /how_it_works: what the HevolveAI learning engine does, as a film and an
// explainer, with the numbers we measured.
//
// OUTCOMES ONLY. The engine is closed technology and its patent filing is in
// progress, so nothing on this page may say how it is built. HowItWorks.test.js
// reads this file, film.js and facts.js and fails on the mechanism words.
//
// Prerendered like the other SEO pages, so it is imported eagerly. The film is
// not: ExplainerPlayer loads ./film (remotion + its player) as a separate chunk
// after mount, and shows a still poster until then. The prerenderer is an
// automated browser and never loads the film, so the saved HTML holds the
// poster, which is exactly what React's first render produces. That is what
// keeps this page free of the hydration mismatch prerender.js documents.
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import SeoHeader from '../../components/shared/SeoHeader';
import SeoFooter from '../../components/shared/SeoFooter';
import PageMeta from '../../components/shared/PageMeta';
import {
  LIVE, LIVE_READ_ON, LIVE_OK_PCT, LIVE_HARMFUL_PCT, CONFIDENCE, CONFIDENCE_PCT,
  EVENT_SECONDS, RETENTION, BENCHMARK_POST, fmtInt,
} from './facts';

const ACCENT = '#8b82ff';
const GREEN = '#17c07f';
const DIM = 'rgba(255,255,255,0.74)';
const FAINT = 'rgba(255,255,255,0.5)';
const PROSE = { color: DIM, fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.75, maxWidth: 760, mt: 1.5 };

function Poster({ title }) {
  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, textAlign: 'center', px: 2 }}>
      <Box component="svg" viewBox="0 0 48 48" sx={{ width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 } }} aria-hidden="true">
        <circle cx="24" cy="24" r="22" fill="none" stroke="rgba(207,201,255,0.6)" strokeWidth="2" />
        <path d="M19 15 L35 24 L19 33 Z" fill="#cfc9ff" />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.6rem' }, color: '#fff' }}>{title}</Typography>
    </Box>
  );
}

// A figure that plays a scene of the film. See the header for why the player
// arrives after mount and never during prerender.
export function ExplainerPlayer({ scene, title, caption }) {
  const [FilmPlayer, setFilmPlayer] = React.useState(null);
  React.useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return undefined;
    let alive = true;
    import(/* webpackChunkName: "how-it-works-film" */ './film')
      .then((m) => { if (alive) setFilmPlayer(() => m.FilmPlayer); })
      .catch(() => { /* the poster stays; the text carries the page */ });
    return () => { alive = false; };
  }, []);
  return (
    <Box component="figure" sx={{ m: 0, mt: 3 }}>
      <Box
        data-testid={`figure-${scene}`}
        sx={{
          position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 3, overflow: 'hidden',
          background: '#0B0A12', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      >
        {FilmPlayer ? <FilmPlayer scene={scene} title={title} /> : <Poster title={title} />}
      </Box>
      {caption ? (
        <Typography component="figcaption" sx={{ color: FAINT, fontSize: '0.85rem', mt: 1.25, lineHeight: 1.6 }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function Section({ id, n, kicker, title, children }) {
  return (
    <Box component="section" id={id} sx={{ pt: { xs: 7, md: 10 } }}>
      <Typography variant="overline" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: 2 }}>
        {n} · {kicker}
      </Typography>
      <Typography component="h2" variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5, fontSize: { xs: '1.6rem', md: '2.1rem' } }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children }) {
  return <Typography sx={PROSE}>{children}</Typography>;
}

function Stat({ value, label, color, testid }) {
  return (
    <Box data-testid={testid} sx={{ height: '100%', p: 3, borderRadius: 3, background: 'linear-gradient(160deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.09)' }}>
      <Typography sx={{ fontSize: { xs: '2.2rem', md: '2.6rem' }, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ color: DIM, mt: 1, lineHeight: 1.55 }}>{label}</Typography>
    </Box>
  );
}

const LOOP = [
  { name: 'Sense', body: "It takes in what your node can see and hear: the screen, a camera, the microphone, your agents' replies and your corrections. Only the inputs you switch on." },
  { name: 'Predict', body: 'Before the next moment arrives, it makes a guess about it.' },
  { name: 'Compare', body: 'Then reality arrives. The difference between its guess and what actually happened is the only teacher it needs.' },
  { name: 'Learn', body: 'It makes a small update right there, on your machine, and moves on to the next moment.' },
];

const NOT_YET = [
  { title: 'Learning between two nodes, live', body: 'The parts work separately. The test that would show two nodes together beating either one alone has not been run yet.' },
  { title: 'Speed', body: `On a home CPU a learning event takes seconds, not milliseconds: a median of ${EVENT_SECONDS} s on the PC above. It runs in the background, so you never wait for it, but it limits how many moments are learned per minute.` },
  { title: 'Every update helping', body: `${LIVE_HARMFUL_PCT}% of measured updates made its guess worse. We count them and publish the count.` },
  { title: 'Hosted frontier models', body: 'The benchmark compares systems we could run under one protocol on one machine. Hosted frontier models are not in that table yet.' },
];

export default function HowItWorks() {
  return (
    <>
      <PageMeta
        title="How HevolveAI Learns: On Your Machine, Nothing Forgotten | Hevolve"
        description="A short film and explainer of the learning engine inside Nunba and HART OS: it learns on your own machine from what you allow it to see, keeps what it learns, and checks its own work. With the numbers we measured."
        ogTitle="How HevolveAI learns"
        ogDescription="It learns on your own machine from what you allow it to see, and keeps what it learns. A short film, with the numbers we measured."
        path="/how_it_works"
      />
      <SeoHeader />
      <Box
        data-testid="how-it-works"
        sx={{
          minHeight: '70vh', color: '#fff', pt: { xs: 6, md: 8 }, pb: 10,
          background: 'radial-gradient(1200px 620px at 80% -10%, rgba(124,114,255,0.16), transparent 60%), radial-gradient(900px 520px at 5% 2%, rgba(52,211,255,0.07), transparent 55%), #0B0A12',
        }}
      >
        <Container maxWidth="md">
          {/* Hero */}
          <Stack spacing={2}>
            <Typography variant="overline" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: 2 }}>
              How it works · the learning engine
            </Typography>
            <Typography
              component="h1"
              variant="h2"
              sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '2.4rem', md: '3.6rem' }, background: 'linear-gradient(120deg, #ffffff, #cfc9ff 55%, #9fe9d0)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
            >
              How HevolveAI learns
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, color: 'rgba(255,255,255,0.86)', lineHeight: 1.6, maxWidth: 760 }}>
              Most AI is trained once in a data centre and frozen before it reaches you.
              HevolveAI keeps learning on your own machine, from what you allow it to see,
              and it keeps what it learns.
            </Typography>
            <Box sx={{ mt: 1, p: 2.5, borderRadius: 2.5, borderLeft: `3px solid ${GREEN}`, background: 'rgba(23,192,127,0.07)' }}>
              <Typography sx={{ color: '#d9fff0', lineHeight: 1.65 }}>
                <b>In one line:</b> it predicts each next moment, learns from the gap between
                its guess and what really happened, and writes each new skill where it cannot
                overwrite the old ones.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                component={Link}
                to="/download?c=how_it_works"
                data-testid="hiw-download"
                variant="contained"
                sx={{ background: GREEN, color: '#04140d', '&:hover': { background: '#12a86e' }, borderRadius: 3, px: 3.5, py: 1.25, textTransform: 'none', fontWeight: 800 }}
              >
                Get Nunba, free
              </Button>
              <Button
                component={Link}
                to="/hive"
                variant="outlined"
                sx={{ borderColor: 'rgba(255,255,255,0.28)', color: '#fff', '&:hover': { borderColor: '#7c72ff', bgcolor: 'rgba(124,114,255,0.08)' }, borderRadius: 3, px: 3, py: 1.25, textTransform: 'none', fontWeight: 600 }}
              >
                See the hive census
              </Button>
              <Button
                component={Link}
                to={BENCHMARK_POST}
                variant="text"
                sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' }, textTransform: 'none', fontWeight: 600 }}
              >
                Read the benchmark &rarr;
              </Button>
            </Stack>
          </Stack>

          <ExplainerPlayer
            scene="film"
            title="How HevolveAI learns: the film"
            caption="The film, 98 seconds, no sound. Every number in it is listed under Sources at the end of this page."
          />

          <Section id="problem" n="01" kicker="The problem" title="Trained once, then frozen">
            <P>
              A large model is trained once, on data gathered before you ever met it, in a data
              centre you will never see. By the time it reaches you it is frozen. It can read what
              you put in front of it, but when the conversation ends nothing it saw has changed it.
              Tomorrow it meets you as a stranger.
            </P>
            <P>
              Teaching it something new means training it again, and training on something new
              wears away what it knew before. Researchers call this catastrophic forgetting. The
              usual cure is to keep the old data and re-train on it forever, at a cost that grows
              with everything the model has ever seen.
            </P>
            <ExplainerPlayer scene="problem" title="Trained once, then frozen" caption="Illustration." />
          </Section>

          <Section id="loop" n="02" kicker="The loop" title="Sense, predict, compare, learn">
            <P>
              HevolveAI runs one loop, all the time, in the background while Nunba keeps answering you.
            </P>
            <Box component="ol" sx={{ pl: 0, listStyle: 'none', mt: 2.5, mb: 0 }}>
              {LOOP.map((s, i) => (
                <Box component="li" key={s.name} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Typography sx={{ fontFamily: 'monospace', color: ACCENT, fontWeight: 700, minWidth: 22, pt: 0.4 }}>{i + 1}</Typography>
                  <Typography sx={{ color: DIM, lineHeight: 1.7 }}>
                    <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>{s.name}.</Box> {s.body}
                  </Typography>
                </Box>
              ))}
            </Box>
            <P>
              No labelled dataset, no answer sheet and no round trip to a data centre. On the home PC
              we measured, one learning event takes a median of {EVENT_SECONDS} seconds, in the
              background.
            </P>
            <ExplainerPlayer scene="loop" title="The loop" caption="Illustration of the loop. The dot is one moment going round." />
          </Section>

          <Section id="surprise" n="03" kicker="Surprise is the teacher" title="Reality is the answer key">
            <P>
              Nobody writes the right answers down for it. The world supplies them, a moment later.
              When its guess was close there is little to learn. When the world surprised it the gap
              is large, and that is where it learns the most. Over time its guesses move toward
              reality and the gap shrinks.
            </P>
            <ExplainerPlayer scene="surprise" title="Its guess moves toward reality" caption="Illustration. The live measurements are in section 06." />
          </Section>

          <Section id="speeds" n="04" kicker="Two speeds" title="One learner for facts, one for patterns">
            <P>
              Some things should be learned at once. Tell it a door code and it should know it the
              next time you ask, not after a thousand repetitions. Other things only show up over
              time: when your days get busy, which app you open after which, how a room sounds in
              the evening.
            </P>
            <P>
              HevolveAI does both. One part remembers a specific fact from a single exposure and
              recalls it exactly, across restarts. Another learns, more gradually, how your
              environment behaves. They divide the work instead of duplicating it, so neither
              erodes the other.
            </P>
            <ExplainerPlayer scene="speeds" title="Two speeds" caption="Illustration. One-step learning and recall are measured in the benchmark write-up." />
          </Section>

          <Section id="keep" n="05" kicker="Nothing learned is lost" title="New skills cannot overwrite old ones">
            <P>
              Each new skill is written where it cannot overwrite the old ones. Learning task B does
              not disturb task A, there is no store of old data to keep re-training on, and the
              guarantee does not weaken as skills pile up.
            </P>
            <Box sx={{ mt: 3, borderRadius: 2.5, overflowX: 'auto', border: '1px solid rgba(124,114,255,0.22)' }}>
              <Box component="table" data-testid="retention-table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 520, '& th, & td': { px: 2, py: 1.4, textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.07)' }, '& th': { color: FAINT, fontWeight: 600, fontSize: '0.85rem' } }}>
                <thead>
                  <tr>
                    <th>System</th>
                    <th>Old skill after learning a new one</th>
                    <th>Learns from one example</th>
                  </tr>
                </thead>
                <tbody>
                  {RETENTION.map((r) => (
                    <Box component="tr" key={r.name} sx={{ color: r.ours ? GREEN : DIM, fontWeight: r.ours ? 700 : 400, background: r.ours ? 'rgba(23,192,127,0.06)' : 'transparent' }}>
                      <td>{r.name}</td>
                      <td>{r.ours ? `${r.label}, no measurable loss` : `${r.label} worse`}</td>
                      <td>{r.one}</td>
                    </Box>
                  ))}
                </tbody>
              </Box>
            </Box>
            <Typography sx={{ color: FAINT, fontSize: '0.85rem', mt: 1.25, lineHeight: 1.6 }}>
              Same protocol for every system on a 4-core CPU with no GPU: learn task A, learn task B,
              then measure A. Published July 2026. Method and caveats are in{' '}
              <Box component={Link} to={BENCHMARK_POST} sx={{ color: ACCENT }}>the write-up</Box>.
            </Typography>
            <ExplainerPlayer scene="keep" title="Nothing learned is lost" caption="Top: illustration. Bottom: the benchmark above, on a log scale." />
          </Section>

          <Section id="checks" n="06" kicker="It checks its own work" title="Measured on one home PC">
            <P>
              Every update is measured: how wrong its guess on that moment was before the update,
              and after. These figures
              come from the learning engine&rsquo;s own counters on one home PC running Nunba, read
              on {LIVE_READ_ON}.
            </P>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Stat testid="stat-events" value={fmtInt(LIVE.events)} color="#cfc9ff" label="learning events so far, each one in the background while Nunba keeps answering" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stat testid="stat-ok" value={`${LIVE_OK_PCT}%`} color={GREEN} label={`of measured updates did not make its guess on that moment worse. The other ${fmtInt(LIVE.harmful)} of ${fmtInt(LIVE.measured)} did, and are counted.`} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stat testid="stat-confidence" value={`${CONFIDENCE_PCT}%`} color="#34d3ff" label={`of ${CONFIDENCE.events} live check-points from 4 to 9 October, its own stated confidence out-predicted a fixed guess`} />
              </Grid>
            </Grid>
            <P>
              The last figure matters as much as the other two. Before an update is checked, the
              engine says how likely its guess is to be good, and that statement is scored against
              what happened. A learner that cannot tell when it is likely to be wrong cannot be
              trusted with anything that matters.
            </P>
            <ExplainerPlayer scene="checks" title="It checks its own work" />
          </Section>

          <Section id="hive" n="07" kicker="From one node to the hive" title="Skills travel. Your data stays home.">
            <P>
              Every Nunba desktop install runs its own copy of the engine, and each one learns a different
              world: your workflow on a desktop, a robot&rsquo;s own body, the road from a vehicle.
              When you choose to share, what travels between nodes is a learned skill, never your
              raw data.
            </P>
            <P>
              The bar we hold the hive to is strict: the hive must beat every single node, on data
              none of them has seen. Today the pieces are proven one at a time, and the first live
              test between two nodes is next. The network&rsquo;s own figures are on the{' '}
              <Box component={Link} to="/hive" data-testid="hiw-hive-link" sx={{ color: ACCENT }}>hive census</Box>.
            </P>
            <ExplainerPlayer scene="hive" title="From one node to the hive" caption="Illustration." />
          </Section>

          <Section id="not-yet" n="08" kicker="What is not proven yet" title="The list we would rather you see now">
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', mt: 2.5 }}>
              {NOT_YET.map((x) => (
                <Box component="li" key={x.title} sx={{ mb: 2, p: 2.5, borderRadius: 2.5, border: '1px solid rgba(240,178,58,0.28)', background: 'rgba(240,178,58,0.05)' }}>
                  <Typography sx={{ fontWeight: 700, color: '#f4cf8a' }}>{x.title}</Typography>
                  <Typography sx={{ color: DIM, mt: 0.5, lineHeight: 1.65 }}>{x.body}</Typography>
                </Box>
              ))}
            </Box>
            <P>Everything above this list is measured. Everything in it is work still in front of us.</P>
          </Section>

          <Box sx={{ mt: 8, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: 'rgba(23,192,127,0.08)', border: `1px solid ${GREEN}`, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Learns on your machine. Keeps what it learns.</Typography>
            <Button
              component={Link}
              to="/download?c=how_it_works"
              variant="contained"
              sx={{ mt: 2.5, background: GREEN, color: '#04140d', '&:hover': { background: '#12a86e' }, borderRadius: 3, px: 4, py: 1.4, textTransform: 'none', fontWeight: 800 }}
            >
              Get Nunba, free
            </Button>
          </Box>

          <Box component="section" id="sources" sx={{ mt: 8, pt: 3, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>Sources</Typography>
            <Box component="ul" sx={{ color: FAINT, fontSize: '0.88rem', lineHeight: 1.7, pl: 2.5, mt: 1 }}>
              <li>
                Live counters: the learning engine&rsquo;s own statistics on one home PC running Nunba,
                read on {LIVE_READ_ON}. {fmtInt(LIVE.events)} learning events in total; {fmtInt(LIVE.measured)} with
                a before-and-after error measurement, of which {fmtInt(LIVE.harmful)} made the guess worse.
              </li>
              <li>
                Confidence: {CONFIDENCE.events} logged check-points on the same PC from 4 to 9 October 2026.
                At each one the engine&rsquo;s running score of its stated confidence was compared with a
                fixed guess at the base rate; it was ahead at {CONFIDENCE.beat}.
              </li>
              <li>Event time: median of 23 consecutive learning events after a restart on 8 October 2026.</li>
              <li>
                Benchmark: <Box component={Link} to={BENCHMARK_POST} sx={{ color: ACCENT }}>The Node That Keeps What It Learns</Box>, July 2026.
              </li>
              <li>
                The animations in sections 01 to 04 and 07, and the top half of 05, are illustrations
                of the idea, not recordings. The numbers in sections 05 and 06 are measurements.
              </li>
              <li>
                The learning engine is our own closed technology, and its patent filing is in progress.
                This page describes what it does and what we measured, not how it is built.
              </li>
            </Box>
          </Box>
        </Container>
      </Box>
      <SeoFooter />
    </>
  );
}
